import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api';
import { LatexText } from '../latex';
import { QuestionLine } from '../question-line';
import type { QuestionLineData } from '../question-line';
import { ItemTagActions, useUserTags, isSystemTag } from '../user-tags';
import { flattenIds, getQuestionIdsForSubject } from '../user-tags';
import type { UserTagData } from '../user-tags';
import { LoadingSpinner, PageHeader, DataTable, useContextMenu, useConfirm, type ColumnDef } from '../components';
import { useLogger, WebComponentCode } from '../logging';
import './BookmarksPage.css';

/** A tag is "shared" (system pool) if the backend flagged it, or its name is a
 *  registered system tag — covers personal copies created before system-tag
 *  routing, so they still read as shared. */
const isSharedTag = (t: UserTagData): boolean =>
  !!t.isSystem || isSystemTag(t.tag.trim().toLowerCase().replace(/\s+/g, '-'));

/** Sentinel tag → the "All" pill: aggregates every tag (deduped by id). */
const ALL_TAG = '__all__';

/**
 * Right-click a tag pill to delete the tag.
 *
 * There is otherwise no way to get rid of a tag once made — only to empty it
 * item by item, which leaves the pill behind anyway. Right-click rather than a
 * visible ✕ because these pills are a dense filter strip and a per-pill button
 * would both crowd it and invite mis-clicks on what is normally a filter.
 *
 * DELETE /user/tags/{tag} removes the tag document alone. The questions and
 * submissions it pointed at live in their own containers and are untouched —
 * they simply stop carrying this tag, which is what the confirmation promises.
 *
 * Shared/system pools are left out: they are not the caller's to delete, and
 * the endpoint is user-scoped, so offering it would silently do nothing.
 */
function useTagPillMenu(
  api: { fetchApi: <T>(endpoint: string, options?: RequestInit) => Promise<T> },
  onDeleted: (tag: string) => void,
) {
  const queryClient = useQueryClient();
  const { openMenu, menuNode } = useContextMenu();
  const { confirm, dialog } = useConfirm();

  const pillMenu = (t: UserTagData, itemCount: number) => (e: React.MouseEvent) => {
    if (isSharedTag(t)) return;
    openMenu(e, [{
      key: 'delete-tag',
      label: 'Delete tag',
      danger: true,
      onSelect: async () => {
        const ok = await confirm({
          message: `Delete the tag "${t.tag}"? The ${itemCount} item${itemCount === 1 ? '' : 's'} in it are not deleted — they only stop being tagged.`,
          confirmLabel: 'Delete tag',
          confirmVariant: 'danger',
        });
        if (!ok) return;
        await api.fetchApi(`/user/tags/${encodeURIComponent(t.tag)}`, { method: 'DELETE' });
        queryClient.invalidateQueries({ queryKey: ['user-tags'] });
        onDeleted(t.tag);
      },
    }]);
  };

  return { pillMenu, tagMenuNodes: <>{menuNode}{dialog}</> };
}

interface QuestionListResponse {
  questions: QuestionLineData[];
  total: number;
  page: number;
  pageSize: number;
}

interface BookmarksPageProps {
  onQuestionClick?: (questionId: string) => void;
  onSubmissionClick?: (submissionId: string) => void;
}

type ItemTab = 'questions' | 'submissions';

export function BookmarksPage({ onQuestionClick, onSubmissionClick }: BookmarksPageProps) {
  const api = useApi();
  const [activeItemTab, setActiveItemTab] = useState<ItemTab>('questions');
  const [selectedQuestionTag, setSelectedQuestionTag] = useState<string | null>(null);
  const [selectedSubmissionTag, setSelectedSubmissionTag] = useState<string | null>(null);

  const { visibleTags: allTags, isLoading: tagsLoading } = useUserTags();

  // Tags with questions
  const tagsWithQuestions = useMemo(() =>
    allTags.filter(t => flattenIds(t.questions).size > 0),
    [allTags],
  );

  // Tags with submissions
  const tagsWithSubmissions = useMemo(() =>
    allTags.filter(t => flattenIds(t.submissions || {}).size > 0),
    [allTags],
  );

  const totalQuestions = useMemo(() =>
    tagsWithQuestions.reduce((sum, t) => sum + flattenIds(t.questions).size, 0),
    [tagsWithQuestions],
  );

  const totalSubmissions = useMemo(() =>
    tagsWithSubmissions.reduce((sum, t) => sum + flattenIds(t.submissions || {}).size, 0),
    [tagsWithSubmissions],
  );

  // On first load, if Questions has no content but Submissions does, default
  // to Submissions. After that, leave the user's choice alone — don't flip
  // them back if they un-tag everything in the active tab.
  const initialTabSet = useRef(false);
  useEffect(() => {
    if (initialTabSet.current || tagsLoading) return;
    initialTabSet.current = true;
    if (tagsWithQuestions.length === 0 && tagsWithSubmissions.length > 0) {
      setActiveItemTab('submissions');
    }
  }, [tagsLoading, tagsWithQuestions.length, tagsWithSubmissions.length]);

  if (tagsLoading) {
    return (
      <div className="bookmarks-page">
        <div className="bookmarks-loading"><LoadingSpinner /></div>
      </div>
    );
  }

  if (tagsWithQuestions.length === 0 && tagsWithSubmissions.length === 0) {
    return (
      <div className="bookmarks-page">
        <PageHeader title="Bookmarks" />
        <div className="bookmarks-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
          <p>No tagged items yet</p>
          <p className="bookmarks-empty-hint">Tag questions or submissions while practicing to see them here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bookmarks-page">
      <PageHeader title="Bookmarks" />

      {/* Item type tabs */}
      <div className="bookmarks-item-tabs">
        <button
          className={`bookmarks-item-tab ${activeItemTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveItemTab('questions')}
        >
          Questions {totalQuestions > 0 && <span className="bookmarks-tab-count">{totalQuestions}</span>}
        </button>
        <button
          className={`bookmarks-item-tab ${activeItemTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveItemTab('submissions')}
        >
          Submissions {totalSubmissions > 0 && <span className="bookmarks-tab-count">{totalSubmissions}</span>}
        </button>
      </div>

      {activeItemTab === 'questions' && (
        <QuestionsTab
          allTags={allTags}
          tagsWithQuestions={tagsWithQuestions}
          selectedTag={selectedQuestionTag}
          onSelectTag={setSelectedQuestionTag}
          onQuestionClick={onQuestionClick}
          api={api}
        />
      )}

      {activeItemTab === 'submissions' && (
        <SubmissionsTab
          tagsWithSubmissions={tagsWithSubmissions}
          selectedTag={selectedSubmissionTag}
          onSelectTag={setSelectedSubmissionTag}
          onSubmissionClick={onSubmissionClick}
          api={api}
        />
      )}
    </div>
  );
}

// ── Questions Tab ────────────────────────────────────────────────────────

function QuestionsTab({
  allTags,
  tagsWithQuestions,
  selectedTag,
  onSelectTag,
  onQuestionClick,
  api,
}: {
  allTags: UserTagData[];
  tagsWithQuestions: UserTagData[];
  selectedTag: string | null;
  onSelectTag: (tag: string) => void;
  onQuestionClick?: (questionId: string) => void;
  api: { fetchApi: <T>(endpoint: string, options?: RequestInit) => Promise<T> };
}) {
  // "All" aggregates every question tag (deduped); otherwise a single tag.
  const activeTag = selectedTag ?? ALL_TAG;
  const isAll = activeTag === ALL_TAG;
  const activeTagData = allTags.find(t => t.tag === activeTag);

  // Question ids grouped by subject for the active selection (one tag, or all).
  const bySubject = useMemo(() => {
    const src = isAll ? tagsWithQuestions : activeTagData ? [activeTagData] : [];
    const m = new Map<string, Set<string>>();
    for (const t of src) {
      for (const subject of Object.keys(t.questions)) {
        const set = m.get(subject) ?? new Set<string>();
        for (const id of getQuestionIdsForSubject(t.questions, subject)) set.add(id);
        m.set(subject, set);
      }
    }
    return m;
  }, [isAll, tagsWithQuestions, activeTagData]);

  const subjects = useMemo(() => [...bySubject.keys()].sort(), [bySubject]);

  // Fetch ONLY the bookmarked questions for the active selection (one batch by IDs).
  // The `?ids=` fast path on getQuestionList skips the importStatus filter, so
  // questions whose status changed (live → draft/rejected) still render — the
  // user bookmarked them, they should see them.
  const activeTagIds = useMemo(() => {
    const s = new Set<string>();
    for (const set of bySubject.values()) for (const id of set) s.add(id);
    return [...s];
  }, [bySubject]);

  // Distinct question count across all tags (the "All" pill badge).
  const distinctQuestionCount = useMemo(() => {
    const s = new Set<string>();
    for (const t of tagsWithQuestions) for (const id of flattenIds(t.questions)) s.add(id);
    return s.size;
  }, [tagsWithQuestions]);

  // Collapsible subject groups (expanded by default).
  const [collapsedSubjects, setCollapsedSubjects] = useState<Set<string>>(new Set());
  const toggleSubject = (s: string) => setCollapsedSubjects(prev => {
    const next = new Set(prev);
    next.has(s) ? next.delete(s) : next.add(s);
    return next;
  });

  const idsKey = activeTagIds.join(',');

  const { data: bookmarkedQuestionsData, isLoading: questionsLoading } = useQuery({
    queryKey: ['bookmark-questions-by-ids', idsKey],
    queryFn: () => api.fetchApi<QuestionListResponse>(`/question-list?ids=${idsKey}`),
    enabled: activeTagIds.length > 0,
    staleTime: 5 * 60_000,
  });

  const questionsMap = useMemo(() => {
    const map = new Map<string, QuestionLineData>();
    for (const q of bookmarkedQuestionsData?.questions ?? []) {
      map.set(q.id, q);
    }
    return map;
  }, [bookmarkedQuestionsData]);

  // Lazy ghost-prune. When the by-ids fetch comes back with fewer rows than
  // the tag references, the missing ids are bookmarks pointing to deleted
  // questions. Serially `DELETE /user/tags/:tag/items/question/:id` for each
  // — sequential because every call replaces the same tag doc, and parallel
  // calls would race. The server auto-deletes the tag doc when it empties.
  // Fire-and-forget; failures just leave the ghost for next page load.
  const queryClient = useQueryClient();
  const logger = useLogger(WebComponentCode.WEB_UI, 'bookmarks');
  // Deleting the tag currently filtered to would leave the view pinned to a tag
  // that no longer exists, so fall back to All.
  const { pillMenu, tagMenuNodes } = useTagPillMenu(api, deleted => {
    if (deleted === activeTag) onSelectTag(ALL_TAG);
  });
  const prunedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (isAll || !activeTag || !bookmarkedQuestionsData) return; // prune targets a single tag
    const present = new Set(bookmarkedQuestionsData.questions.map(q => q.id));
    const missing = activeTagIds.filter(id => !present.has(id));
    if (missing.length === 0) return;
    const pruneKey = `${activeTag}|${missing.join(',')}`;
    if (prunedRef.current.has(pruneKey)) return;
    prunedRef.current.add(pruneKey);
    logger.warn('Bookmark tag references ids missing from question containers', {
      tag: activeTag,
      tagSize: activeTagIds.length,
      missingCount: missing.length,
      missingIds: missing,
    });
    (async () => {
      for (const id of missing) {
        try {
          await api.fetchApi(
            `/user/tags/${encodeURIComponent(activeTag)}/items/question/${encodeURIComponent(id)}`,
            { method: 'DELETE' },
          );
        } catch { /* fire-and-forget */ }
      }
      queryClient.invalidateQueries({ queryKey: ['user-tags'] });
    })();
  }, [isAll, activeTag, activeTagIds, bookmarkedQuestionsData, api, queryClient, logger]);

  const questionTagsMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const t of allTags) {
      for (const qId of flattenIds(t.questions)) {
        const existing = map.get(qId);
        if (existing) existing.push(t.tag);
        else map.set(qId, [t.tag]);
      }
    }
    return map;
  }, [allTags]);

  if (tagsWithQuestions.length === 0) {
    return <div className="bookmarks-tab-empty">No tagged questions yet</div>;
  }

  return (
    <>
      <div className="bookmarks-tags">
        <button
          className={`bookmarks-tag-pill ${isAll ? 'active' : ''}`}
          onClick={() => onSelectTag(ALL_TAG)}
        >
          All ({distinctQuestionCount})
        </button>
        {tagsWithQuestions.map(t => (
          <button
            key={t.tag}
            className={`bookmarks-tag-pill ${t.tag === activeTag ? 'active' : ''} ${isSharedTag(t) ? 'bookmarks-tag-pill--system' : ''}`}
            onClick={() => onSelectTag(t.tag)}
            onContextMenu={pillMenu(t, flattenIds(t.questions).size)}
          >
            {isSharedTag(t) ? <span className="bookmarks-tag-badge">shared</span> : null}{t.tag} ({flattenIds(t.questions).size})
          </button>
        ))}
      </div>
      {tagMenuNodes}

      {questionsLoading ? (
        <div className="bookmarks-loading"><LoadingSpinner /></div>
      ) : (
        <div className="bookmarks-subjects">
          {subjects.map(subject => {
            const subjectIds = bySubject.get(subject) ?? new Set<string>();
            const subjectQuestions = [...subjectIds]
              .map(id => {
                const q = questionsMap.get(id);
                if (!q) return null;
                const tags = questionTagsMap.get(id);
                return tags ? { ...q, tags } : q;
              })
              .filter((q): q is QuestionLineData => !!q);

            if (subjectQuestions.length === 0) return null;

            const isCollapsed = collapsedSubjects.has(subject);
            return (
              <div key={subject} className="bookmarks-subject-group">
                <button
                  type="button"
                  className="bookmarks-subject-title"
                  onClick={() => toggleSubject(subject)}
                  aria-expanded={!isCollapsed}
                >
                  <span className={`bookmarks-subject-chev ${isCollapsed ? 'is-collapsed' : ''}`} aria-hidden="true">▾</span>
                  {subject}
                  <span className="bookmarks-subject-count">{subjectQuestions.length}</span>
                </button>
                {!isCollapsed && (
                <div className="bookmarks-question-list">
                  {subjectQuestions.map(q => (
                    <QuestionLine
                      key={q.id}
                      question={q}
                      onClick={onQuestionClick}
                      renderHoverAction={(qId) => (
                        <ItemTagActions itemType="question" itemId={qId} compact />
                      )}
                    />
                  ))}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Submissions Tab ──────────────────────────────────────────────────────

interface SubmissionSummary {
  id: string;
  questionId: string;
  questionText?: string;
  submittedAt: string;
  createdAt?: string;
  canvasImageUrl?: string;
  typedText?: string;
  selectedOption?: string;
  feedback?: {
    score?: number;
    llmAssessment?: {
      overallAssessment?: string;
    };
    computedScores?: {
      correctnessScore?: number;
      approachScore?: number;
    };
  };
  question?: {
    questionText?: string;
    subject?: string;
  };
}

function SubmissionsTab({
  tagsWithSubmissions,
  selectedTag,
  onSelectTag,
  onSubmissionClick,
  api,
}: {
  tagsWithSubmissions: UserTagData[];
  selectedTag: string | null;
  onSelectTag: (tag: string) => void;
  onSubmissionClick?: (submissionId: string) => void;
  api: { fetchApi: <T>(endpoint: string, options?: RequestInit) => Promise<T> };
}) {
  // "All" aggregates every submission tag (deduped); otherwise a single tag.
  const activeTag = selectedTag ?? ALL_TAG;
  const isAll = activeTag === ALL_TAG;
  const activeTagData = tagsWithSubmissions.find(t => t.tag === activeTag);
  const { pillMenu, tagMenuNodes } = useTagPillMenu(api, deleted => {
    if (deleted === activeTag) onSelectTag(ALL_TAG);
  });

  const taggedSubmissionIds = useMemo(() => {
    const src = isAll ? tagsWithSubmissions : activeTagData ? [activeTagData] : [];
    const s = new Set<string>();
    for (const t of src) for (const id of flattenIds(t.submissions || {})) s.add(id);
    return s;
  }, [isAll, tagsWithSubmissions, activeTagData]);

  // Distinct submission count across all tags (the "All" pill badge).
  const distinctSubmissionCount = useMemo(() => {
    const s = new Set<string>();
    for (const t of tagsWithSubmissions) for (const id of flattenIds(t.submissions || {})) s.add(id);
    return s.size;
  }, [tagsWithSubmissions]);

  // Fetch exactly the tagged submissions, by id — the same shape the Questions
  // tab uses (`/question-list?ids=`).
  //
  // This used to fetch the caller's own recent submissions and intersect. Two
  // things were wrong with that: `/submissions` does not exist (it 404'd, so the
  // tab was empty for everyone), and even with the right URL an admin's own
  // submissions can never contain the STUDENT submission they tagged. The
  // server resolves ids across users for admins, own-only for students.
  const submissionIdsKey = useMemo(() => [...taggedSubmissionIds].sort().join(','), [taggedSubmissionIds]);

  const { data: taggedSubmissionsData, isLoading } = useQuery({
    queryKey: ['bookmark-submissions-by-ids', submissionIdsKey],
    queryFn: () => api.fetchApi<SubmissionSummary[]>(`/solution-analyses?ids=${submissionIdsKey}`),
    staleTime: 5 * 60_000,
    enabled: submissionIdsKey.length > 0,
  });

  const taggedSubmissions = useMemo(() => taggedSubmissionsData ?? [], [taggedSubmissionsData]);

  // Collect questionIds from tagged submissions to fetch their text in one batch.
  // (Avoids fetching whole subject catalogs just to look up text for a few ids.)
  const submissionQuestionIds = useMemo(() => {
    if (!taggedSubmissions.length) return [] as string[];
    const set = new Set<string>();
    for (const s of taggedSubmissions) {
      if (s.questionId) set.add(s.questionId);
    }
    return [...set];
  }, [taggedSubmissions]);

  const submissionQuestionsIdsKey = submissionQuestionIds.join(',');

  const { data: submissionQuestionsData } = useQuery({
    queryKey: ['bookmark-submission-questions-by-ids', submissionQuestionsIdsKey],
    queryFn: () => api.fetchApi<{ questions: { id: string; questionText: string }[] }>(
      `/question-list?ids=${submissionQuestionsIdsKey}`,
    ),
    enabled: submissionQuestionIds.length > 0,
    staleTime: 5 * 60_000,
  });

  // Build questionId -> questionText map
  const questionTextMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const question of submissionQuestionsData?.questions ?? []) {
      map.set(question.id, question.questionText);
    }
    return map;
  }, [submissionQuestionsData]);

  const getInputType = (s: SubmissionSummary): string => {
    if (s.canvasImageUrl) return 'Image';
    if (s.typedText) return 'Text';
    if (s.selectedOption) return 'MCQ';
    return '-';
  };

  const getQuestionText = (s: SubmissionSummary): string => {
    return s.questionText || s.question?.questionText || questionTextMap.get(s.questionId) || '';
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const submissionColumns: ColumnDef<SubmissionSummary>[] = [
    {
      key: 'question', header: 'Question', mobile: 'title',
      render: s => (getQuestionText(s)
        ? <LatexText text={getQuestionText(s)} truncate={60} />
        : <span className="bookmarks-sub-question">{s.questionId.substring(0, 8)}...</span>),
    },
    {
      key: 'date', header: 'Date',
      render: s => <span className="bookmarks-datetime">{formatDateTime(s.submittedAt || s.createdAt || '')}</span>,
    },
    {
      key: 'score', header: 'Score',
      render: s => `${s.feedback?.score ?? s.feedback?.computedScores?.correctnessScore ?? '-'}/10`,
    },
    { key: 'accuracy', header: 'Accuracy', render: s => s.feedback?.computedScores?.correctnessScore ?? '-' },
    { key: 'approach', header: 'Approach', render: s => s.feedback?.computedScores?.approachScore ?? '-' },
    { key: 'input', header: 'Input', render: s => <span className="bookmarks-input-badge">{getInputType(s)}</span> },
    {
      key: 'tags', header: '', mobile: 'accessory',
      render: s => (
        <span onClick={e => e.stopPropagation()}>
          <ItemTagActions itemType="submission" itemId={s.id} compact />
        </span>
      ),
    },
  ];

  if (tagsWithSubmissions.length === 0) {
    return <div className="bookmarks-tab-empty">No tagged submissions yet</div>;
  }

  return (
    <>
      <div className="bookmarks-tags">
        <button
          className={`bookmarks-tag-pill ${isAll ? 'active' : ''}`}
          onClick={() => onSelectTag(ALL_TAG)}
        >
          All ({distinctSubmissionCount})
        </button>
        {tagsWithSubmissions.map(t => (
          <button
            key={t.tag}
            className={`bookmarks-tag-pill ${t.tag === activeTag ? 'active' : ''} ${isSharedTag(t) ? 'bookmarks-tag-pill--system' : ''}`}
            onClick={() => onSelectTag(t.tag)}
            onContextMenu={pillMenu(t, flattenIds(t.submissions || {}).size)}
          >
            {isSharedTag(t) ? <span className="bookmarks-tag-badge">shared</span> : null}{t.tag} ({flattenIds(t.submissions || {}).size})
          </button>
        ))}
      </div>
      {tagMenuNodes}

      {isLoading ? (
        <div className="bookmarks-loading"><LoadingSpinner /></div>
      ) : taggedSubmissions.length === 0 ? (
        <div className="bookmarks-tab-empty">No submissions in this tag</div>
      ) : (
        <DataTable<SubmissionSummary>
          columns={submissionColumns}
          data={taggedSubmissions}
          rowKey={s => s.id}
          onRowClick={onSubmissionClick ? s => onSubmissionClick(s.id) : undefined}
          compact
          bordered
          mobileCards
        />
      )}
    </>
  );
}
